var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo, useState, useEffect } from 'react';
import { validatePageContext } from '../services/pageContextValidator';
// Exported for testing — do not use outside of this module or tests.
export function parsePathname(pathname) {
    // -----------------------------------------------------------------------
    // Building-nested routes — must be checked before the bare /buildings/:id
    // pattern to avoid misclassifying /buildings/12/systems as a building show.
    // -----------------------------------------------------------------------
    // /buildings/:building_id/inspection_series[/...]
    const buildingInspectionSeriesMatch = pathname.match(/^\/buildings\/(\d+)\/inspection_series(?:\/|$)/);
    if (buildingInspectionSeriesMatch) {
        const buildingId = parseInt(buildingInspectionSeriesMatch[1], 10);
        return { pageType: 'building_inspection_series', entityId: buildingId, buildingId };
    }
    // /buildings/:building_id/inspections[/...] (not /inspection_series)
    const buildingInspectionsMatch = pathname.match(/^\/buildings\/(\d+)\/inspections(?:\/|$)/);
    if (buildingInspectionsMatch) {
        const buildingId = parseInt(buildingInspectionsMatch[1], 10);
        return { pageType: 'building_inspections', entityId: buildingId, buildingId };
    }
    // -----------------------------------------------------------------------
    // Building-nested contact/deficiency/report SHOW pages
    // (more specific — require /buildings/:id/X/:numericId — checked before
    //  the listing patterns which use (?:\/|$) and would match any path)
    // -----------------------------------------------------------------------
    const buildingContactShowMatch = pathname.match(/^\/buildings\/(\d+)\/contacts\/(\d+)/);
    if (buildingContactShowMatch) {
        return {
            pageType: 'contact',
            entityId: parseInt(buildingContactShowMatch[2], 10),
            buildingId: parseInt(buildingContactShowMatch[1], 10)
        };
    }
    const buildingDeficiencyShowMatch = pathname.match(/^\/buildings\/(\d+)\/deficiencies\/(\d+)/);
    if (buildingDeficiencyShowMatch) {
        return {
            pageType: 'deficiency',
            entityId: parseInt(buildingDeficiencyShowMatch[2], 10),
            buildingId: parseInt(buildingDeficiencyShowMatch[1], 10)
        };
    }
    const buildingReportShowMatch = pathname.match(/^\/buildings\/(\d+)\/reports\/(\d+)/);
    if (buildingReportShowMatch) {
        return {
            pageType: 'report',
            entityId: parseInt(buildingReportShowMatch[2], 10),
            buildingId: parseInt(buildingReportShowMatch[1], 10)
        };
    }
    // -----------------------------------------------------------------------
    // Building-nested DEVICE show pages (Phase B)
    // These require a numeric device ID as the second path segment.
    // Checked before the listing patterns (building_systems, building_assets,
    // building_equipments) because those use (?:\/|$) which would also match
    // /buildings/12/systems/14 — the device show URL.
    // -----------------------------------------------------------------------
    const deviceSubtypePatterns = [
        [/^\/buildings\/(\d+)\/back_flows\/(\d+)/, 'back_flow'],
        [/^\/buildings\/(\d+)\/fire_dampers\/(\d+)/, 'fire_damper'],
        [/^\/buildings\/(\d+)\/valves\/(\d+)/, 'valve'],
        [/^\/buildings\/(\d+)\/inspectors_test_valves\/(\d+)/, 'inspectors_test_valve'],
        [/^\/buildings\/(\d+)\/dry_valves\/(\d+)/, 'dry_valve'],
        [/^\/buildings\/(\d+)\/building_dry_valves\/(\d+)/, 'dry_valve'],
        [/^\/buildings\/(\d+)\/cylinders\/(\d+)/, 'cylinder'],
        [/^\/buildings\/(\d+)\/equipments\/(\d+)/, 'equipment'],
        [/^\/buildings\/(\d+)\/assets\/(\d+)/, 'asset'],
        [/^\/buildings\/(\d+)\/systems\/(\d+)/, 'system']
    ];
    const deviceEntry = deviceSubtypePatterns.find(([regex]) => pathname.match(regex));
    if (deviceEntry) {
        const [regex, deviceType] = deviceEntry;
        const match = pathname.match(regex);
        return {
            pageType: 'device',
            entityId: parseInt(match[2], 10),
            buildingId: parseInt(match[1], 10),
            deviceType
        };
    }
    // /buildings/:building_id/building_systems or /systems/listing or /clean_agent_systems/...
    const buildingSystemsMatch = pathname.match(/^\/buildings\/(\d+)\/(?:building_systems|systems|clean_agent_systems|alarm_systems)(?:\/|$)/);
    if (buildingSystemsMatch) {
        const buildingId = parseInt(buildingSystemsMatch[1], 10);
        return { pageType: 'building_systems', entityId: buildingId, buildingId };
    }
    // /buildings/:building_id/building_assets or /assets/...
    const buildingAssetsMatch = pathname.match(/^\/buildings\/(\d+)\/(?:building_assets|assets)(?:\/|$)/);
    if (buildingAssetsMatch) {
        const buildingId = parseInt(buildingAssetsMatch[1], 10);
        return { pageType: 'building_assets', entityId: buildingId, buildingId };
    }
    // /buildings/:building_id/equipments/...
    const buildingEquipmentsMatch = pathname.match(/^\/buildings\/(\d+)\/equipments(?:\/|$)/);
    if (buildingEquipmentsMatch) {
        const buildingId = parseInt(buildingEquipmentsMatch[1], 10);
        return { pageType: 'building_equipments', entityId: buildingId, buildingId };
    }
    // Building-nested listing pages (Phase C)
    const buildingContactsMatch = pathname.match(/^\/buildings\/(\d+)\/contacts(?:\/|$)/);
    if (buildingContactsMatch) {
        const buildingId = parseInt(buildingContactsMatch[1], 10);
        return { pageType: 'building_contacts', entityId: buildingId, buildingId };
    }
    const buildingDeficienciesMatch = pathname.match(/^\/buildings\/(\d+)\/deficiencies(?:\/|$)/);
    if (buildingDeficienciesMatch) {
        const buildingId = parseInt(buildingDeficienciesMatch[1], 10);
        return { pageType: 'building_deficiencies', entityId: buildingId, buildingId };
    }
    const buildingReportsMatch = pathname.match(/^\/buildings\/(\d+)\/reports(?:\/|$)/);
    if (buildingReportsMatch) {
        const buildingId = parseInt(buildingReportsMatch[1], 10);
        return { pageType: 'building_reports', entityId: buildingId, buildingId };
    }
    // -----------------------------------------------------------------------
    // Entity show pages — /entity/:id (no further meaningful path segment)
    // -----------------------------------------------------------------------
    const buildingShowMatch = pathname.match(/^\/buildings\/(\d+)(?:\/|$)/);
    if (buildingShowMatch) {
        return { pageType: 'building', entityId: parseInt(buildingShowMatch[1], 10) };
    }
    const inspectionMatch = pathname.match(/^\/inspections\/(\d+)/);
    if (inspectionMatch) {
        return { pageType: 'inspection', entityId: parseInt(inspectionMatch[1], 10) };
    }
    const workOrderMatch = pathname.match(/^\/work_orders\/(\d+)/);
    if (workOrderMatch) {
        return { pageType: 'work_order', entityId: parseInt(workOrderMatch[1], 10) };
    }
    const proposalMatch = pathname.match(/^\/proposals\/(\d+)/);
    if (proposalMatch) {
        return { pageType: 'proposal', entityId: parseInt(proposalMatch[1], 10) };
    }
    const accountMatch = pathname.match(/^\/accounts\/(\d+)/);
    if (accountMatch) {
        return { pageType: 'account', entityId: parseInt(accountMatch[1], 10) };
    }
    // Standalone /inspection_series/:id show page (building-nested handled above)
    const inspectionSeriesShowMatch = pathname.match(/^\/inspection_series\/(\d+)(?:\/|$)/);
    if (inspectionSeriesShowMatch) {
        return { pageType: 'inspection_series', entityId: parseInt(inspectionSeriesShowMatch[1], 10) };
    }
    // Standalone entity show pages (Phase B)
    const deficiencyMatch = pathname.match(/^\/deficiencies\/(\d+)/);
    if (deficiencyMatch) {
        return { pageType: 'deficiency', entityId: parseInt(deficiencyMatch[1], 10) };
    }
    const contactMatch = pathname.match(/^\/contacts\/(\d+)/);
    if (contactMatch) {
        return { pageType: 'contact', entityId: parseInt(contactMatch[1], 10) };
    }
    const invoiceMatch = pathname.match(/^\/invoices\/(\d+)/);
    if (invoiceMatch) {
        return { pageType: 'invoice', entityId: parseInt(invoiceMatch[1], 10) };
    }
    const reportMatch = pathname.match(/^\/reports\/(\d+)/);
    if (reportMatch) {
        return { pageType: 'report', entityId: parseInt(reportMatch[1], 10) };
    }
    // -----------------------------------------------------------------------
    // Listing / index pages — no entity selected
    // -----------------------------------------------------------------------
    if (pathname === '/buildings' || pathname === '/buildings/') {
        return { pageType: 'buildings_index', entityId: 0 };
    }
    if (pathname === '/inspections' || pathname === '/inspections/') {
        return { pageType: 'inspections_index', entityId: 0 };
    }
    if (pathname === '/work_orders' || pathname === '/work_orders/') {
        return { pageType: 'work_orders_index', entityId: 0 };
    }
    if (pathname === '/proposals' || pathname === '/proposals/') {
        return { pageType: 'proposals_index', entityId: 0 };
    }
    if (pathname === '/accounts' || pathname === '/accounts/') {
        return { pageType: 'accounts_index', entityId: 0 };
    }
    if (pathname === '/reports' || pathname === '/reports/') {
        return { pageType: 'reports_index', entityId: 0 };
    }
    if (pathname.startsWith('/inspection_series/listing')) {
        return { pageType: 'inspection_series_index', entityId: 0 };
    }
    if (pathname.startsWith('/inspection_assistant_review')) {
        return { pageType: 'ai_to_review_inspections', entityId: 0 };
    }
    // New listing pages (Phase B)
    if (pathname === '/deficiencies' || pathname === '/deficiencies/') {
        return { pageType: 'deficiencies_index', entityId: 0 };
    }
    if (pathname === '/contacts' || pathname === '/contacts/') {
        return { pageType: 'contacts_index', entityId: 0 };
    }
    if (pathname === '/invoices' || pathname === '/invoices/') {
        return { pageType: 'invoices_index', entityId: 0 };
    }
    // -----------------------------------------------------------------------
    // Dashboard / overview pages
    // -----------------------------------------------------------------------
    if (pathname === '/dashboard' || pathname === '/') {
        return { pageType: 'dashboard', entityId: 0 };
    }
    if (pathname === '/calendar' || pathname.startsWith('/calendar/')) {
        return { pageType: 'calendar', entityId: 0 };
    }
    if (pathname === '/map' || pathname.startsWith('/map/')) {
        return { pageType: 'map', entityId: 0 };
    }
    if (pathname === '/advanced_analytics' || pathname.startsWith('/advanced_analytics/')) {
        return { pageType: 'advanced_analytics', entityId: 0 };
    }
    return null;
}
// Page types that carry no building scope (listing/dashboard tier).
// These produce a context without buildingId; territory validation is skipped.
const NO_BUILDING_PAGE_TYPES = new Set([
    'dashboard',
    'calendar',
    'map',
    'advanced_analytics',
    'buildings_index',
    'inspections_index',
    'work_orders_index',
    'proposals_index',
    'accounts_index',
    'reports_index',
    'inspection_series_index',
    'ai_to_review_inspections',
    // Phase B new listing pages
    'deficiencies_index',
    'contacts_index',
    'invoices_index',
    'unknown'
]);
// Page types that source their buildingId from the URL itself (parsed.buildingId).
const BUILDING_NESTED_PAGE_TYPES = new Set([
    'building_systems',
    'building_assets',
    'building_equipments',
    'building_inspections',
    'building_inspection_series',
    // Phase C new building-nested pages
    'building_contacts',
    'building_deficiencies',
    'building_reports'
]);
/**
 * Build raw page context from URL parsing.
 * Does not include territoryId since the URL doesn't carry it;
 * server-rendered context (with territoryId) always wins when provided.
 */
function buildRawContext(overrideBuildingId) {
    const { pathname } = window.location;
    const parsed = parsePathname(pathname);
    if (!parsed) {
        return undefined;
    }
    const { pageType, entityId, buildingId: urlBuildingId, deviceType } = parsed;
    // Listing/dashboard-tier: no entity scope, but still useful topical context
    if (NO_BUILDING_PAGE_TYPES.has(pageType)) {
        return { schemaVersion: 1, pageType };
    }
    // Building show page — buildingId comes directly from the URL
    if (pageType === 'building') {
        return { schemaVersion: 1, pageType: 'building', buildingId: entityId };
    }
    // Building-nested pages — buildingId is surfaced from the URL segment
    if (BUILDING_NESTED_PAGE_TYPES.has(pageType) && urlBuildingId !== undefined) {
        return { schemaVersion: 1, pageType, buildingId: urlBuildingId };
    }
    // Account and standalone inspection_series show pages — no buildingId in URL
    if (pageType === 'account') {
        return { schemaVersion: 1, pageType: 'account', accountId: entityId };
    }
    // Phase B: device show pages — buildingId and deviceType from URL
    if (pageType === 'device' && urlBuildingId !== undefined && deviceType !== undefined) {
        return {
            schemaVersion: 1,
            pageType: 'device',
            buildingId: urlBuildingId,
            deviceId: entityId,
            deviceType
        };
    }
    // Phase B: deficiency/contact/invoice show pages — may carry buildingId from URL
    // (e.g. /buildings/12/deficiencies/5 → deficiency with buildingId from URL)
    if (pageType === 'deficiency') {
        if (urlBuildingId !== undefined) {
            return { schemaVersion: 1, pageType: 'deficiency', buildingId: urlBuildingId, deficiencyId: entityId };
        }
        // Standalone /deficiencies/:id — no buildingId in URL
        return { schemaVersion: 1, pageType: 'deficiency', deficiencyId: entityId };
    }
    if (pageType === 'contact') {
        if (urlBuildingId !== undefined) {
            return { schemaVersion: 1, pageType: 'contact', buildingId: urlBuildingId, contactId: entityId };
        }
        // Standalone /contacts/:id — no buildingId in URL
        return { schemaVersion: 1, pageType: 'contact', contactId: entityId };
    }
    if (pageType === 'invoice') {
        // Invoices don't carry buildingId in URL; need server-rendered context
        return { schemaVersion: 1, pageType: 'invoice', invoiceId: entityId };
    }
    if (pageType === 'report') {
        if (urlBuildingId !== undefined) {
            return { schemaVersion: 1, pageType: 'report', buildingId: urlBuildingId, reportId: entityId };
        }
        // Standalone /reports/:id — no buildingId in URL
        return { schemaVersion: 1, pageType: 'report', reportId: entityId };
    }
    // Entity show pages that need buildingId provided by caller (server-rendered preferred)
    if (pageType === 'inspection') {
        if (!overrideBuildingId)
            return undefined;
        return { schemaVersion: 1, pageType: 'inspection', buildingId: overrideBuildingId, inspectionId: entityId };
    }
    if (pageType === 'work_order') {
        if (!overrideBuildingId)
            return undefined;
        return { schemaVersion: 1, pageType: 'work_order', buildingId: overrideBuildingId, workOrderId: entityId };
    }
    if (pageType === 'proposal') {
        if (!overrideBuildingId)
            return undefined;
        return { schemaVersion: 1, pageType: 'proposal', buildingId: overrideBuildingId, proposalId: entityId };
    }
    if (pageType === 'inspection_series') {
        if (!overrideBuildingId)
            return undefined;
        return {
            schemaVersion: 1,
            pageType: 'inspection_series',
            buildingId: overrideBuildingId,
            inspectionSeriesId: entityId
        };
    }
    return undefined;
}
/**
 * Hook to detect page context from URL and validate against territory restrictions.
 *
 * For building pages, returns the context directly after validation.
 * For inspection/work_order pages, the caller must provide the buildingId
 * since it's not available in the URL.
 *
 * Territory validation:
 * - Unrestricted users: context passes through unchanged
 * - Restricted users: context is validated against permitted territories
 * - If validation fails or can't be performed: context is suppressed (undefined)
 *
 * @param overrideBuildingId - Optional building ID for inspection/work_order pages
 * @param serverContext - Optional server-rendered context with territoryId already populated
 */
export function usePageContext(overrideBuildingId, serverContext) {
    // Capture pathname for dependency tracking - ensures memo invalidates on route changes
    const { pathname } = window.location;
    // Parse context from URL (memoized)
    const rawContext = useMemo(() => buildRawContext(overrideBuildingId), [overrideBuildingId, pathname]);
    // Use server context if provided, otherwise use URL-parsed context
    const inputContext = serverContext !== null && serverContext !== void 0 ? serverContext : rawContext;
    // State for validated context and validation status
    const [validatedContext, setValidatedContext] = useState(undefined);
    const [isValidating, setIsValidating] = useState(true);
    // Validate context whenever input changes
    useEffect(() => {
        let cancelled = false;
        function validate() {
            return __awaiter(this, void 0, void 0, function* () {
                setIsValidating(true);
                try {
                    const result = yield validatePageContext(inputContext);
                    if (!cancelled) {
                        setValidatedContext(result);
                    }
                }
                catch (error) {
                    console.error('[usePageContext] Validation error:', error);
                    if (!cancelled) {
                        // On error, suppress context for safety
                        setValidatedContext(undefined);
                    }
                }
                finally {
                    if (!cancelled) {
                        setIsValidating(false);
                    }
                }
            });
        }
        void validate();
        return () => {
            cancelled = true;
        };
    }, [inputContext]);
    return {
        context: validatedContext,
        isValidating
    };
}
// ---------------------------------------------------------------------------
// Legacy Export (for backward compatibility during migration)
// ---------------------------------------------------------------------------
/**
 * @deprecated Use usePageContext() directly instead.
 *
 * This wrapper now returns { context, isValidating } to match usePageContext.
 * Previously returned only PageContext | undefined, which caused callers to
 * receive undefined during async validation with no way to distinguish
 * "still loading" from "no context available".
 *
 * Migration guide:
 * - Guard rendering with `if (isValidating) return <Loading />`
 * - Or migrate to usePageContext() which has identical return type
 */
export function usePageContextLegacy(overrideBuildingId) {
    return usePageContext(overrideBuildingId);
}
